// Retrospecto das últimas 10 partidas (como a coluna "Últimas 5" do Brasileirão): 10 quadros, da mais antiga, à
// esquerda, para a última, à direita, que ganha um anel. Vitória em vitoria-500 com ✓, derrota em derrota-500 com ✕,
// o ícone sempre em ink-900. Com menos de 10, os quadros que faltam ficam tracejados à esquerda. Tocar abre a lista
// das 10, com data, tipo de sala, placar, parceiros e adversários, cada um com o botão de amizade.
import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { type PartidaDoRetrospecto, type PessoaDaPartida, contagemDoRetrospecto, textoDoRetrospecto } from '@truco6/gamificacao';
import { api } from './api.ts';
import { BotaoAmizade } from './BotaoAmizade.tsx';
import { BotaoPerfil } from './BotaoPerfil.tsx';
import type { Sessao } from './conta.ts';
import { sessaoDasRelacoes } from './relacoes.ts';
import { Rosto } from './Rosto.tsx';

export const lerRetrospecto = (s: Sessao, u: string, detalhe = false) =>
  api<{ partidas: PartidaDoRetrospecto[] }>(s, 'GET', `/jogadores/${encodeURIComponent(u)}/retrospecto${detalhe ? '?detalhe=1' : ''}`);

const DATA = new Intl.DateTimeFormat('pt-BR', { timeZone: 'America/Sao_Paulo', day: '2-digit', month: '2-digit' });
export function rotuloDaSala(p: PartidaDoRetrospecto): string {
  if (p.tipoSala === 'liga') return 'Liga';
  if (p.tipoSala === 'privada') return 'Sala privada';
  if (p.tipoSala === 'campeonato') return 'Campeonato';
  if (p.tipoSala === 'tentos') return p.detalhe?.sala ? `${p.detalhe.sala} · valendo Tentos` : 'Sala valendo Tentos';
  return p.detalhe?.sala ?? 'Sala pública';
}

// Os quadros sozinhos, para o cartão da mesa, o perfil e o perfil público.
export function QuadrosDoRetrospecto({ partidas }: { partidas: PartidaDoRetrospecto[] }) {
  const { vazios } = contagemDoRetrospecto(partidas);
  return (
    <span className="quadros-retrospecto" aria-hidden="true">
      {Array.from({ length: vazios }, (_, i) => <span key={'v' + i} className="quadro vazio" />)}
      {partidas.map((p, i) => (
        <span key={p.partida} className={'quadro ' + (p.resultado === 'V' ? 'vitoria' : 'derrota') + (i === partidas.length - 1 ? ' ultima' : '')}>
          {p.resultado === 'V' ? '✓' : '✕'}
        </span>
      ))}
    </span>
  );
}

export function Retrospecto({ usuario, apelido }: { usuario: string; apelido: string }) {
  const s = sessaoDasRelacoes();
  const [partidas, setPartidas] = useState<PartidaDoRetrospecto[] | null>(null);
  const [aberto, setAberto] = useState(false);
  useEffect(() => {
    let vivo = true;
    if (s) lerRetrospecto(s, usuario).then((r) => { if (vivo) setPartidas(r.partidas); }, () => { if (vivo) setPartidas(null); });
    return () => { vivo = false; };
  }, [usuario, s?.id]);
  if (!partidas) return null;
  const { v, d } = contagemDoRetrospecto(partidas);
  if (!partidas.length) {
    return (
      <div className="retrospecto vazio">
        <span className="rotulo">ÚLTIMAS 10</span>
        <span className="nota">Ainda sem partidas</span>
      </div>
    );
  }
  return (
    <>
      <button className="retrospecto" aria-label={textoDoRetrospecto(apelido, partidas) + ' Toque para ver as partidas.'} onClick={() => setAberto(true)}>
        <span className="cab-retrospecto"><span className="rotulo">ÚLTIMAS 10</span><b>{v} V · {d} D</b></span>
        <QuadrosDoRetrospecto partidas={partidas} />
        <span className="pe-retrospecto"><small>mais antiga</small><small>última</small></span>
      </button>
      {aberto && s && <FolhaDoRetrospecto sessao={s} usuario={usuario} apelido={apelido} aoFechar={() => setAberto(false)} />}
    </>
  );
}

function Pessoas({ titulo, pessoas, bots, eu }: { titulo: string; pessoas: PessoaDaPartida[]; bots: number; eu: string }) {
  if (!pessoas.length && !bots) return null;
  return (
    <div className="lado-partida">
      <small className="rotulo">{titulo}</small>
      <ul className="lista-pessoas">
        {pessoas.map((p) => (
          <li key={p.usuario}>
            <BotaoPerfil usuario={p.usuario} apelido={p.usuario === eu ? 'você' : p.apelido}>
              <Rosto perfil={{ apelido: p.apelido, avatar: p.avatar }} usuario={p.usuario} cor="var(--line-600)" tamanho={36} />
              <span className="nome"><b>{p.usuario === eu ? 'Você' : p.apelido}</b></span>
            </BotaoPerfil>
            {p.usuario !== eu && <BotaoAmizade usuario={p.usuario} apelido={p.apelido} compacto />}
          </li>
        ))}
        {bots > 0 && <li className="bots"><span className="nome"><small>{bots === 1 ? '1 bot' : `${bots} bots`}</small></span></li>}
      </ul>
    </div>
  );
}

function FolhaDoRetrospecto({ sessao, usuario, apelido, aoFechar }: { sessao: Sessao; usuario: string; apelido: string; aoFechar: () => void }) {
  const [partidas, setPartidas] = useState<PartidaDoRetrospecto[] | null>(null);
  useEffect(() => {
    let vivo = true;
    lerRetrospecto(sessao, usuario, true).then((r) => { if (vivo) setPartidas(r.partidas); }, () => { if (vivo) setPartidas([]); });
    return () => { vivo = false; };
  }, [usuario]);
  return createPortal(
    <div className="folha-fundo" onClick={(ev) => { if (ev.target === ev.currentTarget) aoFechar(); }}>
      <div className="folha simples folha-retrospecto" role="dialog" aria-modal="true" aria-labelledby="folha-retrospecto">
        <h2 id="folha-retrospecto">Últimas partidas{usuario === sessao.id ? '' : ` de ${apelido}`}</h2>
        {!partidas && <p className="carregando" role="status">Carregando...</p>}
        {partidas && (
          <ol className="partidas-retrospecto">
            {[...partidas].reverse().map((p) => (
              <li key={p.partida}>
                <div className="cab-partida">
                  <span className={'quadro ' + (p.resultado === 'V' ? 'vitoria' : 'derrota')} aria-hidden="true">{p.resultado === 'V' ? '✓' : '✕'}</span>
                  <span className="quem-partida">
                    <b>{p.resultado === 'V' ? 'Vitória' : 'Derrota'}{p.detalhe ? ` · ${p.detalhe.placar[0]} x ${p.detalhe.placar[1]}` : ''}</b>
                    <small>{DATA.format(new Date(p.terminadaEm))} · {rotuloDaSala(p)}</small>
                  </span>
                </div>
                {p.detalhe && (
                  <>
                    <Pessoas titulo="PARCEIROS" pessoas={p.detalhe.parceiros} bots={p.detalhe.bots.parceiros} eu={sessao.id} />
                    <Pessoas titulo="ADVERSÁRIOS" pessoas={p.detalhe.adversarios} bots={p.detalhe.bots.adversarios} eu={sessao.id} />
                  </>
                )}
              </li>
            ))}
          </ol>
        )}
        <button className="btn2" onClick={aoFechar}>Fechar</button>
      </div>
    </div>,
    document.body,
  );
}
